/**
 * useDashboardToast — one toast at a time for a dashboard, raised by every tab
 * and modal through `notify`. Render `toastLayer` last inside the frame: it
 * sits above the modal scrim (z-toast), so errors raised from an open modal
 * are visible. The timer ref stops an older toast's timeout from hiding a
 * newer one early, and `notify` is stable so it can sit in effect deps.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Toast, ToastKind, ToastLayer } from '../components/ui';

export type DashboardNotify = (kind: 'success' | 'error', title: string, message?: string) => void;

const TOAST_MS = 5000;

export function useDashboardToast(): { notify: DashboardNotify; dismiss: () => void; toastLayer: React.ReactNode } {
  const [toast, setToast] = useState<{ kind: ToastKind; title: string; message?: string } | null>(null);
  const timer = useRef<number | null>(null);

  const dismiss = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    setToast(null);
  }, []);

  const notify = useCallback<DashboardNotify>((kind, title, message) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setToast({ kind, title, message });
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setToast(null);
    }, TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const toastLayer = toast ? (
    <ToastLayer>
      <Toast kind={toast.kind} title={toast.title} message={toast.message} onClose={dismiss} />
    </ToastLayer>
  ) : null;

  return { notify, dismiss, toastLayer };
}
